import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { AdminStoryMedia } from '@erp/shared';
import { Film, GripVertical, ImageIcon, Loader2, Pencil, Play, Trash2, Upload } from 'lucide-react';
import { type DragEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/shadcn/dialog.js';
import { Button, confirmDialog } from '../../components/ui/index.js';

import {
  addStoryMedia,
  fetchStoryMediaObjectUrl,
  patchStoryMedia,
  removeStoryMedia,
  reorderStoryMedia,
  setStoryPoster,
} from './api.js';
import { ImageEditorDialog } from './ImageEditorDialog.js';
import { useObjectUrl } from './useMedia.js';

const IMAGE_MIME = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
const VIDEO_MIME = new Set(['video/mp4', 'video/webm', 'video/quicktime']);
const MAX_IMAGE = 25 * 1024 * 1024;
const MAX_VIDEO = 100 * 1024 * 1024;
/** On-screen durations offered per image slide (seconds). Default is 5s. */
const DURATION_OPTIONS = [3, 5, 7, 10, 15];
const DEFAULT_IMAGE_SEC = 5;

type Pending = { key: string; name: string };

export function MediaManager({
  storyId,
  media,
  onChange,
}: {
  storyId: string;
  media: AdminStoryMedia[];
  onChange: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('stories');
  const { t: tc } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pending, setPending] = useState<Pending[]>([]);
  const pendingSeq = useRef(0);
  const [editing, setEditing] = useState<{ media: AdminStoryMedia; url: string } | null>(null);
  const [poster, setPoster] = useState<AdminStoryMedia | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Uploads stream in behind an optimistic placeholder tile (+ spinner) per file,
  // each removed as its own request settles — so a multi-file drop stays responsive.
  const uploadFiles = async (files: FileList | null): Promise<void> => {
    const list = files ? Array.from(files) : [];
    if (fileRef.current) fileRef.current.value = '';
    const queued: { key: string; file: File }[] = [];
    for (const f of list) {
      const isImage = IMAGE_MIME.has(f.type);
      const isVideo = VIDEO_MIME.has(f.type);
      if (!isImage && !isVideo) { toast.error(t('media.badType', { defaultValue: 'Only images and short videos are allowed.' })); continue; }
      if (f.size > (isVideo ? MAX_VIDEO : MAX_IMAGE)) { toast.error(t('media.tooLarge', { defaultValue: 'That file is too large.' })); continue; }
      queued.push({ key: `u${pendingSeq.current++}`, file: f });
    }
    if (queued.length === 0) return;
    setPending((p) => [...p, ...queued.map(({ key, file }) => ({ key, name: file.name }))]);
    for (const { key, file } of queued) {
      try {
        await addStoryMedia(storyId, file, file.name, file.type);
        await onChange();
      } catch {
        toast.error(t('media.uploadFailed', { defaultValue: "Couldn't upload {{name}}", name: file.name }));
      } finally {
        setPending((p) => p.filter((x) => x.key !== key));
      }
    }
  };

  // Native file drop onto the media area (separate from the dnd-kit tile sort,
  // which is pointer-based). Only react to an actual file drag.
  const onDropFiles = (e: DragEvent<HTMLDivElement>): void => {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    setDragOver(false);
    void uploadFiles(e.dataTransfer.files);
  };
  const onDragOverFiles = (e: DragEvent<HTMLDivElement>): void => {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  };
  const onDragLeaveFiles = (e: DragEvent<HTMLDivElement>): void => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false);
  };

  const onRemove = async (m: AdminStoryMedia): Promise<void> => {
    if (!(await confirmDialog({ message: t('media.confirmRemove', { defaultValue: 'Remove this media?' }), tone: 'danger' }))) return;
    setBusy(true);
    try { await removeStoryMedia(storyId, m.id); await onChange(); } finally { setBusy(false); }
  };

  const setDuration = async (m: AdminStoryMedia, durationMs: number): Promise<void> => {
    try { await patchStoryMedia(storyId, m.id, { durationMs }); await onChange(); }
    catch { toast.error(tc('errors:server')); }
  };

  const onDragEnd = async (e: DragEndEvent): Promise<void> => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = media.map((m) => m.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const next = arrayMove(ids, from, to);
    setBusy(true);
    try { await reorderStoryMedia(storyId, next); await onChange(); } finally { setBusy(false); }
  };

  // Editing an existing image: upload the edited result, splice it into the same
  // slot, then drop the original (keeps order + duration).
  const openEdit = async (m: AdminStoryMedia): Promise<void> => {
    const url = await fetchStoryMediaObjectUrl(storyId, m.id);
    setEditing({ media: m, url });
  };
  const onEditedSave = async (blob: Blob): Promise<void> => {
    if (!editing) return;
    const old = editing.media;
    URL.revokeObjectURL(editing.url);
    setEditing(null);
    setBusy(true);
    try {
      const created = await addStoryMedia(storyId, blob, 'edited.jpg', 'image/jpeg');
      if (old.durationMs != null) await patchStoryMedia(storyId, created.id, { durationMs: old.durationMs });
      const order = media.map((m) => (m.id === old.id ? created.id : m.id));
      await reorderStoryMedia(storyId, order);
      await removeStoryMedia(storyId, old.id);
      await onChange();
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setBusy(false);
    }
  };

  const hasTiles = media.length > 0 || pending.length > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium">{t('media.title', { defaultValue: 'Media' })}</div>
          <p className="text-xs text-fg-muted">{t('media.hint', { defaultValue: 'Photos and short videos, 9:16. Drag to reorder.' })}</p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
          multiple
          className="hidden"
          onChange={(e) => void uploadFiles(e.target.files)}
        />
        <Button type="button" variant="secondary" size="sm" loading={busy} onClick={() => fileRef.current?.click()}>
          <Upload className="h-4 w-4" aria-hidden /> {t('media.add', { defaultValue: 'Add photo / video' })}
        </Button>
      </div>

      <div
        onDragOver={onDragOverFiles}
        onDragLeave={onDragLeaveFiles}
        onDrop={onDropFiles}
        className={`rounded-lg transition ${dragOver ? 'ring-2 ring-brand' : ''}`}
      >
        {!hasTiles ? (
          <div className={`grid place-items-center rounded-lg border border-dashed py-8 text-sm transition ${dragOver ? 'border-brand text-brand' : 'border-border text-fg-muted'}`}>
            {dragOver ? t('media.dropHint', { defaultValue: 'Drop files here to upload' }) : t('media.empty', { defaultValue: 'No media yet' })}
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void onDragEnd(e)}>
            <SortableContext items={media.map((m) => m.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                {media.map((m) => (
                  <MediaTile
                    key={m.id}
                    storyId={storyId}
                    m={m}
                    onRemove={() => void onRemove(m)}
                    onEdit={m.mediaType === 'image' ? () => void openEdit(m) : undefined}
                    onPoster={m.mediaType === 'video' ? () => setPoster(m) : undefined}
                    onDuration={m.mediaType === 'image' ? (ms) => void setDuration(m, ms) : undefined}
                  />
                ))}
                {pending.map((p) => (
                  <UploadingTile key={p.key} name={p.name} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {editing && (
        <ImageEditorDialog
          open
          sourceUrl={editing.url}
          onSave={(b) => void onEditedSave(b)}
          onClose={() => { URL.revokeObjectURL(editing.url); setEditing(null); }}
        />
      )}
      {poster && (
        <PosterPicker
          storyId={storyId}
          media={poster}
          onClose={() => setPoster(null)}
          onSaved={async () => { setPoster(null); await onChange(); }}
        />
      )}
    </div>
  );
}

/** An optimistic placeholder tile shown while a file uploads. */
function UploadingTile({ name }: { name: string }): JSX.Element {
  const { t } = useTranslation('stories');
  return (
    <div className="grid aspect-[9/16] place-items-center overflow-hidden rounded-lg border border-border bg-black">
      <div className="flex w-full flex-col items-center gap-2 px-2 text-center text-white/70">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
        <span className="w-full truncate text-[10px] text-white/60">{name}</span>
        <span className="text-[10px] text-white/50">{t('media.uploading', { defaultValue: 'Uploading…' })}</span>
      </div>
    </div>
  );
}

function MediaTile({
  storyId,
  m,
  onRemove,
  onEdit,
  onPoster,
  onDuration,
}: {
  storyId: string;
  m: AdminStoryMedia;
  onRemove: () => void;
  onEdit?: () => void;
  onPoster?: () => void;
  onDuration?: (durationMs: number) => void;
}): JSX.Element {
  const { t } = useTranslation('stories');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: m.id });
  // Videos preview via their poster (if set); images via the image itself.
  const url = useObjectUrl(
    () => fetchStoryMediaObjectUrl(storyId, m.id, m.mediaType === 'video' && m.posterAttachmentId ? 'poster' : undefined),
    `${m.id}:${m.posterAttachmentId ?? ''}`,
  );
  const targetSec = secondsOf(m);
  const [durSec, setDurSec] = useState(targetSec);
  useEffect(() => { setDurSec(targetSec); }, [targetSec]);
  const durationOptions = DURATION_OPTIONS.includes(durSec) ? DURATION_OPTIONS : [durSec, ...DURATION_OPTIONS].sort((a, b) => a - b);
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="group relative aspect-[9/16] overflow-hidden rounded-lg border border-border bg-black">
      {url ? (
        m.mediaType === 'video' && !m.posterAttachmentId
          ? <video src={url} className="h-full w-full object-cover" muted playsInline />
          : <img src={url} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="grid h-full w-full place-items-center text-white/40">
          {m.mediaType === 'video' ? <Film className="h-6 w-6" aria-hidden /> : <ImageIcon className="h-6 w-6" aria-hidden />}
        </div>
      )}
      {m.mediaType === 'video' && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <Play className="h-8 w-8 text-white/80 drop-shadow" aria-hidden />
        </div>
      )}
      <button
        type="button"
        className="absolute left-1 top-1 cursor-grab rounded bg-black/50 p-1 text-white active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label={t('stories.drag', { defaultValue: 'Drag to reorder' })}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      {onDuration && (
        <select
          value={durSec}
          onChange={(e) => { const s = Number(e.target.value); setDurSec(s); onDuration(s * 1000); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="absolute right-1 top-1 cursor-pointer appearance-none rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white outline-none backdrop-blur-sm focus:ring-2 focus:ring-brand/40"
          aria-label={t('media.duration', { defaultValue: 'Seconds on screen' })}
          title={t('media.duration', { defaultValue: 'Seconds on screen' })}
        >
          {durationOptions.map((s) => (
            <option key={s} value={s} className="bg-bg text-fg">{t('media.secondsShort', { defaultValue: '{{n}}s', n: s })}</option>
          ))}
        </select>
      )}
      <div className="absolute bottom-1 left-1 right-1 flex justify-center gap-1 opacity-0 transition group-hover:opacity-100">
        {onEdit && (
          <Button type="button" variant="secondary" size="sm" className="h-7 px-2" onClick={onEdit} title={t('media.edit', { defaultValue: 'Edit photo' })}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </Button>
        )}
        {onPoster && (
          <Button type="button" variant="secondary" size="sm" className="h-7 px-2" onClick={onPoster} title={t('media.poster', { defaultValue: 'Choose thumbnail' })}>
            <ImageIcon className="h-3.5 w-3.5" aria-hidden />
          </Button>
        )}
        <Button type="button" variant="danger" size="sm" className="h-7 px-2" onClick={onRemove} title={t('media.confirmRemove', { defaultValue: 'Remove' })}>
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

/** Current on-screen seconds for an image tile (schema stores ms; default 5s). */
function secondsOf(m: AdminStoryMedia): number {
  return m.durationMs && m.durationMs > 0 ? Math.round(m.durationMs / 1000) : DEFAULT_IMAGE_SEC;
}

/** Scrub a video and capture the current frame as its poster (thumbnail). */
function PosterPicker({
  storyId,
  media,
  onClose,
  onSaved,
}: {
  storyId: string;
  media: AdminStoryMedia;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('stories');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [saving, setSaving] = useState(false);
  const url = useObjectUrl(() => fetchStoryMediaObjectUrl(storyId, media.id), media.id);

  const capture = async (): Promise<void> => {
    const v = videoRef.current;
    if (!v) return;
    setSaving(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = v.videoWidth || 1080;
      canvas.height = v.videoHeight || 1920;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9));
      if (blob) { await setStoryPoster(storyId, media.id, blob); await onSaved(); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t('media.poster', { defaultValue: 'Choose thumbnail' })}</DialogTitle></DialogHeader>
        <p className="text-sm text-fg-muted">{t('media.posterHint', { defaultValue: 'Scrub to the frame you want, then capture it.' })}</p>
        <div className="mx-auto aspect-[9/16] w-52 overflow-hidden rounded-lg border border-border bg-black">
          {url && <video ref={videoRef} src={url} className="h-full w-full object-contain" controls playsInline />}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>{t('media.close', { defaultValue: 'Close' })}</Button>
          <Button loading={saving} onClick={() => void capture()}>{t('media.posterSet', { defaultValue: 'Use this frame' })}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
